import { useEffect, useRef } from 'react';

import type { ContactDecisionMakerFlowModel } from '../useContactDecisionMakerFlow/types';
import type { useCreateDecisionMakerMessage } from '../useCreateDecisionMakerMessage';

type Params = {
	flow: ContactDecisionMakerFlowModel;
	petitionId: string;
	/** `undefined` when the page has no decision maker to contact, so the flow can never reach `sending`. */
	decisionMakerId: string | undefined;
	/**
	 * The mount's `useCreateDecisionMakerMessage()`; passed in so this hook owns no data access of its
	 * own. Read through a ref, so a caller that rebuilds it on every render cannot restart a send.
	 */
	sendMessage: ReturnType<typeof useCreateDecisionMakerMessage>;
	/** Called once a message has actually gone out, with the id it was stored under. */
	onSent: (messageId: string) => void;
	/**
	 * Called when the server refused the send because it already holds this supporter's message. The
	 * row is done, but nothing went out now, so this is deliberately not `onSent`.
	 */
	onAlreadySent: () => void;
};

/**
 * Performs the send the flow asks for. `Preview` dispatches `send`, but the shared screens are
 * deliberately free of any page's data layer, so the owner of the flow watches for `stage === 'sending'`,
 * runs the mutation, and reports the outcome back into the machine. It must run where the flow state
 * lives, not in the row: a row that remounts mid-send would otherwise see `sending` again and send twice.
 *
 * Every dependency is fixed while the stage is `sending` (the reducer accepts `setDraft` only in
 * `preview:editing`), so the effect runs once per send. The callbacks and `sendMessage` are read
 * through refs: the caller rebuilds the callbacks whenever completion storage refreshes, may rebuild
 * `sendMessage` on any render, and a parent render mid-send must neither restart the send nor resolve
 * into a stale callback.
 */
export function useSendReviewedDraft({
	flow,
	petitionId,
	decisionMakerId,
	sendMessage,
	onSent,
	onAlreadySent,
}: Params): void {
	const { stage, draft } = flow.data;
	const { sendSucceeded, sendFailed } = flow.actions;

	const sendMessageRef = useRef(sendMessage);
	const onSentRef = useRef(onSent);
	const onAlreadySentRef = useRef(onAlreadySent);
	useEffect(() => {
		sendMessageRef.current = sendMessage;
		onSentRef.current = onSent;
		onAlreadySentRef.current = onAlreadySent;
	}, [sendMessage, onSent, onAlreadySent]);

	useEffect(() => {
		if (stage !== 'sending' || !decisionMakerId) return;

		void (async () => {
			const result = await sendMessageRef.current({ petitionId, decisionMakerId, body: draft });

			if (result.status === 'sent') {
				// The message is stored whatever the callback does, so the machine has to advance
				// regardless — a throwing `onSent` would otherwise leave it wedged in `sending`.
				try {
					onSentRef.current(result.messageId);
				} finally {
					sendSucceeded();
				}
				return;
			}

			if (result.failure === 'already_sent') {
				// The server already holds this supporter's message, so the row is done. Still a failure
				// to the machine: the review screen says the message is in rather than jumping to the Sent
				// screen. Guarded like `onSent`: a throwing callback must not leave the machine in `sending`.
				try {
					onAlreadySentRef.current();
				} finally {
					sendFailed(result.failure);
				}
				return;
			}

			// Back to the draft either way; `retryable` decides whether Send is offered again.
			sendFailed(result.failure);
		})();
	}, [stage, draft, petitionId, decisionMakerId, sendSucceeded, sendFailed]);
}
